<template>
  <div
    class="min-h-screen flex items-center justify-center flex-col login-bg">

    <div class="bg-white rounded-[16px] w-[440px] p-[32px] flex flex-col items-center gap-[32px] shadow-md">
      <div class="font-semibold text-[24px] text-primary/80">
        <img src="@/assets/images/logo.png" alt="" class="inline h-[40px] bg-white rounded-full" />
        Web Portal
      </div>
      <h1 class="text-[28px] font-bold text-[#111111] text-center w-full">
        {{ forgotMode ? 'Восстановление пароля' : 'Вход в аккаунт' }}
      </h1>

      <!-- Main Login Form -->
      <form v-if="!forgotMode" @submit.prevent="handleLogin" class="w-full flex flex-col gap-[24px]">
        <!-- Email input -->
        <div class="w-full">
          <div class="w-full border border-[#E3E6E9] rounded-[12px] p-4 h-[56px] flex items-center"
               :class="{ 'border-red-500': emailError }">
            <input
              type="email"
              name="email"
              v-model="email"
              @blur="validateEmail"
              placeholder="Электронная почта"
              class="w-full outline-none text-[15px] text-[#111111] placeholder-[#9AA9B6]"
              required
              :disabled="isLoading"
            />
          </div>
          <p v-if="emailError" class="text-red-500 text-sm mt-1">{{ emailError }}</p>
        </div>

        <!-- Password input -->
        <div class="w-full">
          <div class="w-full border border-[#E3E6E9] rounded-[12px] p-4 h-[56px] flex items-center">
            <input
              :type="showPassword ? 'text' : 'password'"
              v-model="password"
              name="password"
              placeholder="Пароль"
              class="w-full outline-none text-[15px] text-[#111111] placeholder-[#9AA9B6]"
              required
              :disabled="isLoading"
            />
            <button
              type="button"
              @click="showPassword = !showPassword"
              class="text-[#9AA9B6] hover:text-[#40a9ff] focus:outline-none"
            >
              <Eye v-if="showPassword" />
              <EyeOff v-else />
            </button>
          </div>
        </div>

        <!-- Forgot password link -->
        <div v-if="false" class="w-full text-right">
          <button
            type="button"
            @click="toggleForgotMode"
            class="text-[#1890FF] text-sm hover:underline focus:outline-none"
          >
            Забыли пароль?
          </button>
        </div>

        <!-- Login button -->
        <div class="w-full mt-2">
          <button
            type="submit"
            class="w-full bg-[#1890FF] text-white font-semibold py-4 px-8 rounded-[12px] text-[15px] flex justify-center items-center gap-2 hover:bg-[#40a9ff] transition-colors"
            :disabled="isLoading"
          >
            <svg v-if="isLoading" class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Войти
          </button>
        </div>
      </form>

      <!-- Forgot Password Form -->
      <form v-else @submit.prevent="handleForgotPassword" class="w-full flex flex-col gap-[24px]">
        <!-- Email input -->
        <div class="w-full">
          <div class="text-[15px] text-[#111111] mb-4">
            Введите вашу электронную почту для получения ссылки на сброс пароля
          </div>
          <div class="w-full border border-[#E3E6E9] rounded-[12px] p-4 h-[56px] flex items-center"
               :class="{ 'border-red-500': emailError }">
            <input
              type="email"
              v-model="email"
              @blur="validateEmail"
              placeholder="Электронная почта"
              class="w-full outline-none text-[15px] text-[#111111] placeholder-[#9AA9B6]"
              required
              :disabled="isLoading"
            />
          </div>
          <p v-if="emailError" class="text-red-500 text-sm mt-1">{{ emailError }}</p>
        </div>

        <!-- Back to login link -->
        <div class="w-full text-right">
          <button
            type="button"
            @click="toggleForgotMode"
            class="text-[#1890FF] text-sm hover:underline focus:outline-none"
          >
            Вернуться к входу
          </button>
        </div>

        <!-- Submit button -->
        <div class="w-full mt-2">
          <button
            type="submit"
            class="w-full bg-[#1890FF] text-white font-semibold py-4 px-8 rounded-[12px] text-[15px] flex justify-center items-center gap-2 hover:bg-[#40a9ff] transition-colors"
            :disabled="isLoading"
          >
            <svg v-if="isLoading" class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Отправить ссылку
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from '@/components/ui/toast/use-toast';
import { login, requestPasswordReset, me } from '@/api/auth';
import { Eye, EyeOff} from 'lucide-vue-next';
import { useAuthStore } from '@/store/auth';
import { useSettingsStore } from '@/store/settings';

const authStore = useAuthStore();
const router = useRouter();
const { toast } = useToast();

const email = ref('admin@robi.solutions');
const password = ref('adminadmin');
const isLoading = ref(false);
const showPassword = ref(false);
const forgotMode = ref(false);
const emailError = ref('');

const validateEmail = () => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!email.value) {
    emailError.value = '';
    return;
  }

  if (!emailRegex.test(email.value)) {
    emailError.value = 'Пожалуйста, введите корректный email';
  } else {
    emailError.value = '';
  }
};

const toggleForgotMode = () => {
  forgotMode.value = !forgotMode.value;
  emailError.value = '';
};

const handleLogin = async () => {
  if (isLoading.value || emailError.value) return;

  validateEmail();
  if (emailError.value) return;

  isLoading.value = true;

  try {
    const result = await authStore.login(email.value, password.value);
    if (result) {
     router.push('/');
    }
  } catch (error) {
    console.error('Login error:', error);
    toast({
      title: error.response?.data?.message || 'Упс! Что-то пошло не так!',
      variant: 'destructive'
    });
  } finally {
    isLoading.value = false;
  }
};

const handleForgotPassword = async () => {
  if (isLoading.value || emailError.value) return;

  validateEmail();
  if (emailError.value) return;

  isLoading.value = true;

  try {
    const response = await requestPasswordReset(email.value);

    if (response.status === 200) {
      toast({
        title: 'Ссылка для сброса пароля отправлена на вашу почту',
        variant: 'success'
      });
      toggleForgotMode();
    } else {
      toast({
        title: response.data?.message || 'Не удалось отправить ссылку на сброс пароля',
        variant: 'destructive'
      });
    }
  } catch (error) {
    console.error('Password reset request error:', error);
    toast({
      title: error.response?.data?.message || 'Упс! Что-то пошло не так!',
      variant: 'destructive'
    });
  } finally {
    isLoading.value = false;
  }
};


onMounted(() => {
  return () => {
  };
});
</script>

<style scoped>
.login-bg {
  background-image: url('@assets/images/login-bg.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0px 1000px #ffffff inset !important;
  box-shadow: 0 0 0px 1000px #ffffff inset !important;
  -webkit-text-fill-color: #000 !important;
  transition: background-color 5000s ease-in-out 0s;
}
</style>
